import axios from "axios";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { getProfileData } from "../services/ProfileService";
import { setLoader } from "../Store/Reducers/UserReducer";
import TreatmentWithUs from "./MedicalHistory Component/TreatmentWithUs";
import { useContextMenu } from "../Layout Component/ContextMenu";
import { toast } from "react-toastify";
import { copyText } from "../utils/printDoc";
import { readEntries } from "./vitalsStore";

// ── Landing page ─────────────────────────────────────────────────────────────
//
// What the patient sees on sign-in. It follows the clinical OX.ar patient
// overview: a strip of counts, the patient and their partner side by side,
// and the treatment cycles underneath.
//
// PATIENT-SAFE BY DESIGN. Everything here is the patient's own information.
// Clinic notes and activity notes are not requested or shown, and the results
// count is of APPROVED results only (see approvedSets in ObservationResult.js
// for the same rule applied to the results pages).

const DEFAULT_AVATAR = null;

const isBlank = (v) => v === null || v === undefined || ["", "n/a", "na", "null"].includes(String(v).trim().toLowerCase());
const dateOnly = (raw) => {
  if (isBlank(raw) || String(raw).startsWith("0001-01-01")) return null;
  const m = moment(raw);
  return m.isValid() && m.year() > 1 ? m : null;
};

// Same weight-band wording the clinical overview prints under the BMI.
const dobText = (raw, age) => {
  const d = dateOnly(raw);
  return d ? `${d.format("DD-MMM-YYYY")}${isBlank(age) ? "" : ` (${age}y)`}` : null;
};

const RELATIONSHIP = {
  1: "Legally single",
  2: "Separated",
  3: "Married / civil partner to the person having treatment",
  4: "Married / civil partner to another person",
  5: "Divorced / partnership dissolved",
  6: "Widowed / surviving civil partner",
};

const firstOf = (obj, keys) => {
  for (const k of keys) if (!isBlank(obj?.[k])) return obj[k];
  return null;
};

function bmiBand(bmi) {
  if (bmi < 18.5) return "Underweight";
  if (bmi < 25) return "Normal";
  if (bmi < 30) return "Overweight";
  return "Obese";
}

function bmiOf(q) {
  const h = Number(q?.heightinMetres);
  const w = Number(q?.weightinKilograms);
  if (!(h > 0) || !(w > 0)) return null;
  const v = w / (h * h);
  return Number.isFinite(v) ? v : null;
}

function initials(first, last) {
  return `${(first || "").charAt(0)}${(last || "").charAt(0)}`.toUpperCase() || "?";
}

function Avatar({ image, first, last }) {
  if (image) {
    return <img className="ox-avatar" src={`data:image/png;base64,${image}`} alt="" />;
  }
  return <span className="ox-avatar ox-avatar--initials" aria-hidden="true">{initials(first, last)}</span>;
}

function PersonCard({ role, person, bmi, allergies, vitals, active, compact, expanded, onToggle, loading, onOpen, onMenu, onActivate, hint }) {
  if (!person) {
    return (
      <section className="ox-person ox-person--empty" aria-label={role}>
        <span className="ox-role">{role}</span>
        <p className="ox-muted">{loading ? "Loading…" : "No partner details on record."}</p>
      </section>
    );
  }
  const detail = (label) => (person.details || []).find(([k]) => k === label)?.[1];
  const name = [person.first, person.last].filter(Boolean).join(" ") || person.fullname;
  const clinic = detail("Clinic");
  const facts = [detail("Date of birth"), detail("Sex"), detail("MRN") && `MRN ${detail("MRN")}`, detail("NHS / CHI") && `NHS/CHI ${detail("NHS / CHI")}`].filter((x) => !isBlank(x));
  const contact = [detail("Email"), detail("Mobile")].filter((x) => !isBlank(x));
  const more = (person.details || []).filter(([k, v]) => !isBlank(v) && !["Date of birth", "Sex", "MRN", "NHS / CHI", "Clinic", "Email", "Mobile"].includes(k));

  return (
    <section
      className={`ox-person ox-person--sm ox-clickable${active ? " ox-person--active" : ""}${compact ? " is-compact" : ""}`}
      aria-label={role}
      tabIndex={0}
      title={hint}
      onClick={onActivate}
      onDoubleClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen && onOpen(); }}
      onContextMenu={onMenu}
    >
      <div className="ox-person__top">
        <Avatar image={person.image} first={person.first} last={person.last} />
        <div className="ox-person__id">
          <div className="ox-person__chips">
            <span className="ox-role">{role}</span>
            {clinic && !compact && <span className="ox-chip ox-chip--clinic">{clinic}</span>}
            {active && <span className="ox-chip ox-chip--viewing">Viewing</span>}
          </div>
          <h2 className="ox-person__name">{name}</h2>
        </div>
        {compact && allergies && (
          allergies.length === 0
            ? <span className="ox-chip ox-chip--ok">No known allergy</span>
            : <span className="ox-chip ox-chip--warn">{allergies.length} allerg{allergies.length === 1 ? "y" : "ies"}</span>
        )}
        {bmi !== null && bmi !== undefined && (
          <div className="ox-bmi">
            <strong>{bmi.toFixed(1)}</strong>
            <span>BMI &middot; {bmiBand(bmi)}</span>
          </div>
        )}
      </div>

      {!compact && (
        <>
          <p className="ox-person__facts">{facts.map((f, i) => <span key={i}>{f}</span>)}</p>
          {contact.length > 0 && <p className="ox-person__facts">{contact.map((f, i) => <span key={i}>{f}</span>)}</p>}
          {allergies && (
            <p className="ox-person__facts">
              {allergies.length === 0
                ? <span className="ox-chip ox-chip--ok">{"✓"} No known allergy</span>
                : allergies.slice(0, 4).map((x) => <span key={x} className="ox-chip ox-chip--warn">{x}</span>)}
            </p>
          )}
          {(more.length > 0 || (vitals && vitals.length > 0)) && (
            <button type="button" className="ox-person__more" aria-expanded={expanded} onClick={(e) => { e.stopPropagation(); onToggle(); }}>
              {expanded ? "Fewer details" : "More details"}
            </button>
          )}
          {expanded && (
            <>
              <dl className="ox-person__grid">
                {more.map(([label, v]) => (
                  <div key={label}><dt>{label}</dt><dd>{String(v)}</dd></div>
                ))}
              </dl>
              {vitals && vitals.length > 0 && (
                <p className="ox-vitalsrow" aria-label="Latest vitals">
                  {vitals.map(([label, v]) => (
                    <span key={label}><em>{label}</em> {v}</span>
                  ))}
                </p>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}

export default function PatientOverview() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const url = process.env.REACT_APP_OXAR_API_URL;
  const uID = localStorage.getItem("UserID");

  const [profile, setProfile] = useState(null);
  const [partner, setPartner] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [allergies, setAllergies] = useState(null);
  const [counts, setCounts] = useState({ appts: null, drugs: null, results: null });
  const [loading, setLoading] = useState(true);
  const [entries] = useState(readEntries);
  const { show, menu } = useContextMenu();
  const [focus, setFocus] = useState("patient");
  const [compact, setCompact] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // The tiles shrink to a single line once the page is scrolled, and stay pinned.
  useEffect(() => {
    const onScroll = (e) => {
      const t = e && e.target && e.target.scrollTop !== undefined ? e.target.scrollTop : 0;
      const y = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, t);
      setCompact((was) => (was ? y > 12 : y > 70));
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, []);

  useEffect(() => {
    let alive = true;
    const quiet = (p) => p.catch(() => null);

    (async () => {
      dispatch(setLoader(true));
      const [prof, rel, q, al, ap, rx, obs] = await Promise.all([
        quiet(getProfileData(uID)),
        quiet(axios(`${url}api/CrmUser/GetRelatedContactByID?Id=${uID}`)),
        quiet(axios(`${url}api/Profile/GetQuestionairebyPatientId?id=${uID}`)),
        quiet(axios(`${url}api/Allergy/GetByContactId?id=${uID}`)),
        quiet(axios(`${url}api/AppointmentDosage/GetAppointments?contactId=${uID}`)),
        quiet(axios(`${url}api/Prescriptions/GetByContactId?id=${uID}`)),
        // The order list, not the result lines: it carries the approval flag.
        quiet(axios(`${url}api/TDLObservation/GetObservationByContactId?id=${uID}`)),
      ]);
      if (!alive) return;

      const p = prof?.content || null;
      if (p) {
        try { localStorage.setItem("UData", JSON.stringify(p)); } catch { /* convenience only */ }
      }
      setProfile(p);

      const r = rel?.data?.content || null;
      if (r) {
        try { sessionStorage.setItem("ReleatedcontactData", JSON.stringify(r)); } catch { /* convenience only */ }
      }
      setPartner(r);
      setQuestionnaire(q?.data?.content || null);
      setAllergies(
        Array.isArray(al?.data?.content)
          ? al.data.content.map((a) => a.name).filter((n) => !isBlank(n))
          : null,
      );

      const list = (res) => (Array.isArray(res?.data?.content) ? res.data.content : null);
      const appts = list(ap);
      const now = moment();
      setCounts({
        appts: appts ? appts.filter((a) => moment(a.startTime).isSameOrAfter(now, "day")).length : null,
        drugs: list(rx)?.length ?? null,
        // Approved results only — an unapproved result is not the patient's to see yet.
        results: list(obs)?.filter((o) => o.approve === true).length ?? null,
      });
      setLoading(false);
      dispatch(setLoader(false));
    })();

    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const patient = useMemo(() => profile && ({
    first: profile.firstName,
    last: profile.lastName,
    dob: profile.birthday,
    age: profile.age,
    image: profile.entityimage,
    details: [
      ["Date of birth", dobText(profile.birthday, profile.age)],
      ["Sex", profile.gendercodeName],
      ["MRN", firstOf(profile, ["mrn", "MRN", "bcrm_mrn", "clinicNumber", "mrnNumber", "patientNumber"])],
      ["NHS / CHI", profile.nhsNumber],
      ["Clinic", localStorage.getItem("ClinicName")],
      ["Relationship", RELATIONSHIP[profile.relationShipStatus]],
      ["Occupation", profile.occupation],
      ["Ethnicity", profile.ethnicity],
      ["Email", profile.email],
      ["Mobile", profile.mobileNumber],
      ["Home phone", profile.homePhone],
      ["Address", [profile.street, profile.city, profile.state, profile.postalCode, profile.country].filter((x) => !isBlank(x)).join(", ")],
      ["Current GP", [profile.gPsName, profile.gpSurgeryName].filter((x) => !isBlank(x)).join(" \u00b7 ")],
      ["GP contact", [profile.gpTelephoneNumber, profile.gp_email].filter((x) => !isBlank(x)).join(" \u00b7 ")],
      ["GP address", [profile.gpStreet, profile.gpCity, profile.gpState, profile.gpPostalCode, profile.gpCountry].filter((x) => !isBlank(x)).join(", ")],
    ],
  }), [profile]);

  const partnerPerson = useMemo(() => partner && ({
    fullname: partner.fullname,
    first: partner.firstname || partner.firstName,
    last: partner.lastname || partner.lastName,
    image: partner.entityimage,
    bmi: Number(partner.bmi) > 0 ? Number(partner.bmi) : null,
    details: [
      ["Date of birth", dobText(partner.birthdate, partner.age)],
      ["Sex", partner.gender || partner.gendercodeName],
      ["MRN", partner.clinicNumber],
      ["Occupation", partner.occupation],
      ["Ethnicity", partner.ethnicityName],
      ["Email", partner.emailaddress1 || partner.email],
      ["Mobile", partner.mobilephone || partner.mobileNumber],
      ["Address", [partner.address1_line1, partner.address1_city, partner.address1_stateorprovince, partner.address1_country].filter((x) => !isBlank(x)).join(", ")],
    ],
  }), [partner]);

  // Latest vitals: what the patient has recorded, else what the questionnaire holds.
  const vitals = useMemo(() => {
    const latest = (type) => entries.filter((e) => e.type === type).sort((a, b) => new Date(b.date) - new Date(a.date))[0]?.value;
    const weight = latest("weight") ?? (Number(questionnaire?.weightinKilograms) || null);
    const height = latest("height") ?? (Number(questionnaire?.heightinMetres) || null);
    const sys = latest("bpSystolic") ?? (Number(questionnaire?.systolic) || null);
    const dia = latest("bpDiastolic") ?? (Number(questionnaire?.diastolic) || null);
    const oxygen = latest("oxygen");
    const hr = latest("heartRate");
    const temp = latest("temperature");
    return [
      weight && ["Weight", `${weight} kg`],
      height && ["Height", `${height} m`],
      sys && dia && ["BP", `${sys}/${dia} mmHg`],
      oxygen && ["SpO\u2082", `${oxygen}%`],
      hr && ["HR", `${hr} bpm`],
      temp && ["Temp", `${temp} \u00b0C`],
    ].filter(Boolean);
  }, [entries, questionnaire]);

  const stat = (n, label, to) => (
    <button type="button" className="ox-stat" onClick={() => navigate(to)}>
      <strong>{n === null ? "–" : n}</strong> {label}
    </button>
  );

  // Opens the questionnaire to READ; Edit is there once the patient is.
  const viewMyHistory = () => navigate("/MedicalHistory");
  const openPartner = () => navigate("/PartnerDetails");

  const copy = async (text, what) => {
    const ok = await copyText(text);
    (ok ? toast.success : toast.error)(ok ? `${what} copied` : "Could not copy", { autoClose: 1500, hideProgressBar: true });
  };
  const nameOf = (p) => [p?.first, p?.last].filter(Boolean).join(" ") || p?.fullname || "";
  const detailOf = (p, label) => (p?.details || []).find(([k]) => k === label)?.[1];

  const patientMenu = (e) => {
    setFocus("patient");
    show(e, [
      { label: "Open my details", hint: "double-click", onClick: viewMyHistory },
      { label: "Make this the active member", disabled: true },
      { divider: true },
      { label: "Appointments", onClick: () => navigate("/Appointment") },
      { label: "Medical history & questionnaire", onClick: viewMyHistory },
      { label: "Documents", onClick: () => navigate("/Documents") },
      { divider: true },
      { label: "Copy name", onClick: () => copy(nameOf(patient), "Name") },
      { label: "Copy NHS / MRN", disabled: !detailOf(patient, "NHS / CHI") && !detailOf(patient, "MRN"), onClick: () => copy([detailOf(patient, "NHS / CHI"), detailOf(patient, "MRN")].filter(Boolean).join(" / "), "NHS / MRN") },
    ]);
  };
  const partnerMenu = (e) => {
    show(e, [
      { label: "Open partner details", hint: "double-click", onClick: openPartner },
      { label: "Make this the active member", disabled: focus === "partner", onClick: () => setFocus("partner") },
      { divider: true },
      { label: "Appointments", onClick: () => navigate("/Appointment") },
      { divider: true },
      { label: "Copy name", onClick: () => copy(nameOf(partnerPerson), "Name") },
      { label: "Copy MRN", disabled: !detailOf(partnerPerson, "MRN"), onClick: () => copy(detailOf(partnerPerson, "MRN"), "MRN") },
    ]);
  };

  return (
    <div className="ox-overview" onContextMenu={(e) => show(e, [
      { label: "Refresh", onClick: () => window.location.reload() },
      { label: "Print", onClick: () => window.print() },
    ])}>
      <div className="ox-strip" role="group" aria-label="At a glance">
        {stat(counts.appts, "upcoming appts", "/Appointment")}
        {stat(counts.drugs, "prescriptions", "/Prescription")}
        {stat(counts.results, "approved results", "/ObservationResult")}
        <span className="ox-strip__who" />
        <button type="button" className="ox-btn" onClick={() => window.location.reload()}>Refresh</button>
      </div>

      <div className={`ox-people${compact ? " is-compact" : ""}`}>
        <PersonCard
          role="Patient"
          person={patient}
          bmi={bmiOf(questionnaire)}
          allergies={allergies}
          vitals={vitals}
          active={focus === "patient"}
          compact={compact}
          expanded={expanded}
          onToggle={() => setExpanded((x) => !x)}
          loading={loading}
          hint="Double-click to open your details. Right-click for more."
          onActivate={() => setFocus("patient")}
          onOpen={viewMyHistory}
          onMenu={patientMenu}
        />
        <button type="button" className="ox-switch" title="Switch between you and your partner" aria-label="Switch between you and your partner"
          onClick={() => setFocus((f) => (f === "patient" ? "partner" : "patient"))}>
          <i className="fa-solid fa-right-left" aria-hidden="true" />
        </button>
        <PersonCard
          role="Partner"
          person={partnerPerson}
          bmi={partnerPerson?.bmi}
          active={focus === "partner"}
          compact={compact}
          expanded={expanded}
          onToggle={() => setExpanded((x) => !x)}
          loading={loading}
          hint="Double-click to see your partner's details. Right-click for more."
          onActivate={() => setFocus("partner")}
          onOpen={openPartner}
          onMenu={partnerMenu}
        />
      </div>

      <p className="ox-hint ox-overview__hint">
        Double-click your details to view or edit your medical history and questionnaire, or your partner's to see theirs. Double-click a treatment to see its
        folliculogram and images. Right-click anything for more options.
      </p>

      <section className="ox-treatments" aria-label="Treatments">
        <TreatmentWithUs standalone embedded />
      </section>
      {menu}
    </div>
  );
}
